import { effect, Injectable, signal, WritableSignal } from '@angular/core';
import { User, UserView } from '@core/models/entities/user.models';
import { BehaviorSubject, finalize, map, Observable, tap } from 'rxjs';
import { HttpApi } from './http.api';
import { environment } from '@environment';
import {
  ActivationInstructor,
  ActivationInstructorSuccesss,
  Instructor,
  RegistrationInstructor,
  RegistrationInstructorSuccesss,
  RegistrationUser,
  Specialities,
} from '@core/models/entities/instructor.models';
import { HttpClient } from '@angular/common/http';
import { WlcmPaginatedData, WlcmQueryParams } from '@wlcm/common';
import { WlcmAutocompleteOption } from '@wlcm/angular/core';
import { AuthApiService } from '@core/services/api/auth.api.service';
import { UserRole } from '@core/models/enums/user.enum';
import { base64ToFormData } from '@core/utils/base64.utils';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ProfileApiService {
  loading: WritableSignal<boolean> = signal(false);

  registerStep: WritableSignal<RegistrationInstructorSuccesss | null> = signal(null);

  instructor: WritableSignal<Instructor | null> = signal(null);

  registrationUser: WritableSignal<RegistrationUser | null> = signal(null);

  readonly userRole = UserRole;

  private readonly baseUrl: string = environment.apiUrl;
  private _fetcher$: Observable<User> | null = null;
  private readonly _user$ = new BehaviorSubject<User | null>(null);

  constructor(
    private readonly httpApi: HttpApi,
    private http: HttpClient,
    private authApi: AuthApiService,
    private router: Router
  ) {
    effect(
      () => {
        const instructor = this.instructor();
        if (instructor && !instructor.is_active) {
          this.router.navigate(['/auth/sign-up-instructor-update']);
        }
      },
      { allowSignalWrites: true }
    );
  }

  get user$(): Observable<User | null> {
    if (this.user) return this._user$.asObservable();
    if (this._fetcher$) return this._fetcher$;
    this._fetcher$ = new Observable<User>(observer => this.fetch().subscribe(observer));
    return this._fetcher$;
  }

  get user(): User | null {
    return this._user$.value;
  }

  set user(user: User | null) {
    this._user$.next(user);
  }

  protected fetch(): Observable<User> {
    return this.httpApi
      .get<User>('user')
      .pipe(tap((user: User) => this._user$.next(user)))
      .pipe(finalize(() => (this._fetcher$ = null)));
  }

  patch(user: Partial<User>) {
    const currentUser = this.user || ({} as User);
    this._user$.next({ ...currentUser, ...user });
  }

  isIntakeFormCompleted(): boolean {
    // const { role } = this.user || ({} as User);
    // if (role !== Role.PARENT) return true;
    // return !!(this.user as Parent).info;
    return false;
  }

  getUser(): Observable<User> {
    const url = this.authApi.userType() === this.userRole.instructor ? 'instructor/me' : 'user';
    return this.httpApi.get<User>(`${this.baseUrl}/${url}`);
  }

  patchUser(payload: UserView): Observable<User> {
    return this.httpApi.patch<User>(`${this.baseUrl}/user`, payload);
  }

  deleteUser(): Observable<number> {
    return this.httpApi.delete<number>(`${this.baseUrl}/user`);
  }

  registrationInstructor(payload: RegistrationInstructor): Observable<RegistrationInstructorSuccesss> {
    return this.http.patch<RegistrationInstructorSuccesss>(`${this.baseUrl}/instructor/me/registration`, payload);
  }

  activationInstructor(payload: ActivationInstructor): Observable<ActivationInstructorSuccesss> {
    const data = new FormData();
    data.append('avatar', base64ToFormData(payload.avatar));
    data.append('bio', payload.bio);
    payload.specialities.forEach(res => {
      data.append('specialities', res);
    });
    return this.httpApi.patch<ActivationInstructorSuccesss>(`${this.baseUrl}/instructor/me/activation`, data);
  }

  getInstructor(): Observable<Instructor> {
    return this.httpApi.get<Instructor>(`${this.baseUrl}/instructor/me`).pipe(
      map(res => {
        this.instructor.set(res);
        return res;
      })
    );
  }

  patchInstructor(payload: FormData): Observable<Instructor> {
    return this.httpApi.patch<Instructor>(`${this.baseUrl}/instructor/me`, payload).pipe(
      map(res => {
        this.instructor.set(res);
        return res;
      })
    );
  }

  deleteInstructor(): Observable<number> {
    return this.httpApi.delete<number>(`${this.baseUrl}/instructor/me`);
  }

  getSpecialities(params: WlcmQueryParams): Observable<WlcmPaginatedData<Specialities>> {
    return this.httpApi.get<WlcmPaginatedData<Specialities>>(`${this.baseUrl}/speciality`, { params });
  }

  fetchSpecialities = (params: WlcmQueryParams): Observable<WlcmPaginatedData<WlcmAutocompleteOption>> => {
    return this.getSpecialities(params).pipe(
      map((paginated: WlcmPaginatedData<Specialities>) => {
        return {
          ...paginated,
          data: paginated.data.map((res: Specialities) => this.specialitiesToOption(res)),
        };
      })
    );
  };

  specialitiesToOption = (item: Specialities): WlcmAutocompleteOption => {
    return new WlcmAutocompleteOption(item.id, item.name, item.name, item);
  };
}
